import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { hash, array } from '@ember/helper';
import { task } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';
import { and, eq, or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import { HashicorpCloudNetwork20200907AzureHopType } from '@clients/cloud-network';
import {
  HdsPageHeader,
  HdsAlert,
  HdsFormTextInputField,
  HdsFormSelectField,
  HdsLinkInline,
  HdsButtonSet,
  HdsButton,
  HdsTable,
  HdsIcon,
  HdsBadge,
  HdsFormToggleGroup,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  Typography,
  Flex,
  WithErrors,
  DisclosureGuide,
} from 'core/components';
import { boxPadding, boxMargin, tooltip } from 'core/modifiers';
import { ROUTE_SUBMITTED } from 'core/utils/consts/analytics-events/platform';
import {
  urlFor,
  classNames,
  resourceTypeFor,
  optionForSourceState,
  resourceTypeDisplayName,
} from 'core/helpers';
import { TYPE_NETWORK_ROUTE } from 'common/utils/cloud-resource-types';
import { HVN_ROUTE_NAMES } from 'networks-common/utils/network-routes';
import { isAzureProviderNetwork } from 'networks-common/utils/networks';
import {
  AWS_CONNECTIONS,
  AWS_CONNECTIONS_INFO,
  AWS_DISCLOSURE_GUIDES_PEERING,
  AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS,
} from 'networks-common/utils/aws-connections';
import UpNext from 'networks-common/components/networks/cards/up-next';

/**
 *
 * `model` will be a model containing the network and network connections (Peering or TGW);
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnRoutesCreate` renders the HVN Routes Create page.
 *
 *
 * ```
 * <Page::HvnRoutes::Create
 *   @model={{@model}}
 * />
 * ```
 *
 * @class PageHvnRoutesCreate
 *
 */
export default class PageHvnRoutesCreate extends Component {
  azureHopType = HashicorpCloudNetwork20200907AzureHopType;

  @service analytics;
  @service api;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked errors;
  @tracked id;
  @tracked cidr;
  @tracked routeName = this.router.currentRouteName;
  @tracked connectionTarget = null;
  @tracked useHubSpoke = false;
  @tracked nextHopType = this.azureHopType.APPLIANCE;
  @tracked nextHopIP;

  hvnRouteNames = HVN_ROUTE_NAMES;
  connectionsInfo = AWS_CONNECTIONS_INFO;

  checkElementValidity(element) {
    return element !== '';
  }

  get resourceType() {
    return TYPE_NETWORK_ROUTE;
  }

  get connection() {
    //Check the model to see if a connection is in the model
    let model = this.args.model;
    let connection = null;
    AWS_CONNECTIONS.filter((item) => {
      if (item in model) {
        return (connection = {
          id: model[item]?.id,
          ...AWS_CONNECTIONS_INFO[item],
        });
      }
    });
    return connection;
  }

  get disclosureGuideCidr() {
    if (this.connectionTarget) {
      if (this.connectionTarget.type === AWS_CONNECTIONS_INFO.peering.type) {
        return AWS_DISCLOSURE_GUIDES_PEERING.cidrBlock;
      }
      if (
        this.connectionTarget.type === AWS_CONNECTIONS_INFO.tgwAttachment.type
      ) {
        return AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS.cidrBlock;
      }
    }
    if (this.connection.type) {
      if (this.connection.type === AWS_CONNECTIONS_INFO.peering.type) {
        return AWS_DISCLOSURE_GUIDES_PEERING.cidrBlock;
      }
      if (this.connection.type === AWS_CONNECTIONS_INFO.tgwAttachment.type) {
        return AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS.cidrBlock;
      }
    }
    return null;
  }

  get isAzure() {
    return isAzureProviderNetwork(this.args.model.network);
  }

  get showHubSpokeConfig() {
    return (
      variation('hcp-ui-azure-hub-spoke') &&
      this.isAzure &&
      this.isHubSpokePeering
    );
  }

  get isAzureRouteConfigured() {
    return this.args.model.routes?.some((a) => a.azureRoute);
  }

  get isHubSpokePeering() {
    //model differs between network details -create route and peering connections-create hvn route
    const peering = this.args.model.peerings?.length
      ? this.args.model.peerings[0]
      : this.args.model.peering;
    const { useRemoteGateways = false, allowForwardedTraffic = false } =
      peering?.target?.azureTarget ?? {};
    return useRemoteGateways || allowForwardedTraffic;
  }
  get showHubSpokeWarning() {
    return !this.isAzureRouteConfigured && this.isHubSpokePeering;
  }

  get isDisabled() {
    if (
      this.connectionTargets?.peeringTargets.length === 0 &&
      this.connectionTargets?.tgwAttachmentTargets.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  }

  get connectionTargets() {
    //This is needed for the select options if creating a Route from the Routes tab
    let { tgwAttachments, peerings } = this.args.model;
    if (tgwAttachments || peerings) {
      let peeringTargets = peerings.filter((peering) => {
        if (!peering.target?.hvnTarget) {
          return (
            peering.state === 'ACTIVE' || peering.state === 'PENDING_ACCEPTANCE'
          );
        }
      });

      let tgwAttachmentTargets = tgwAttachments.filter(
        (tgw) => tgw.state === 'ACTIVE' || tgw.state === 'PENDING_ACCEPTANCE'
      );
      peeringTargets;
      tgwAttachmentTargets;
      return { peeringTargets, tgwAttachmentTargets };
    }
    return null;
  }

  toggleUseHubSpoke = () => {
    this.useHubSpoke = !this.useHubSpoke;
  };

  updateConnectionTarget = (event) => {
    let selectedIndex = event.target.selectedIndex;
    let optGroup = event.target[selectedIndex].parentNode;
    if (optGroup.id === AWS_CONNECTIONS_INFO.tgwAttachment.type) {
      this.connectionTarget = {
        id: event.target.value,
        type: optGroup.id,
        ...AWS_CONNECTIONS_INFO.tgwAttachment,
      };
    }
    if (optGroup.id === AWS_CONNECTIONS_INFO.peering.type) {
      this.connectionTarget = {
        id: event.target.value,
        type: optGroup.id,
        ...AWS_CONNECTIONS_INFO.peering,
      };
    }
  };

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();
    let {
      location: { organizationId, projectId },
      id: hvnId,
    } = this.args.model.network;
    let { location } = this.args.model.network;
    let { cidr, id } = this;
    let payload;
    let target;
    let configPath;
    if (this.connection) {
      //target needed to create HVN Route
      target = {
        hvnConnection: {
          type: this.connection.type,
          id: this.connection.id,
          location: location,
        },
      };
      //part of ember route path needed for View Configuration instructions link in success flash message
      configPath = this.connection.configPath;
    }
    if (this.connectionTarget) {
      //target needed to create HVN Route
      target = {
        hvnConnection: {
          type: this.connectionTarget.type,
          id: this.connectionTarget.id,
          location: location,
        },
      };
      //part of ember route path needed for View Configuration instructions link in success flash message
      configPath = this.connectionTarget.configPath;
    }

    payload = {
      id: id,
      destination: cidr,
      target: target,
      azureRoute: this.useHubSpoke
        ? {
            nextHopType: this.nextHopType,
            nextHopIpAddress: this.nextHopIP,
          }
        : undefined,
    };

    this.analytics.trackEvent(ROUTE_SUBMITTED, payload);

    await this.api.network.createHVNRoute(
      organizationId,
      projectId,
      hvnId,
      payload
    );
    let successTitle = this.intl.t(
      'components.page.hvn-routes.create.success.title'
    );
    this.flashMessages.success(successTitle, {
      content: this.intl.t(
        'components.page.hvn-routes.create.success.message',
        {
          htmlSafe: true,
          configurationLink: `/orgs/${organizationId}/projects/${projectId}/hvns/${hvnId}/${configPath}/${target.hvnConnection.id}/configuration-instructions`,
        }
      ),
    });
    //if creating an HVN Route from a connections (TGW or Peering) Route table, take the user back to that connection detail page
    if (this.connection) {
      await this.router
        .transitionTo(
          this.connection.detail,
          organizationId,
          projectId,
          hvnId,
          target.hvnConnection.id
        )
        .followRedirects();
    } else {
      await this.router
        .transitionTo(
          'cloud.orgs.detail.projects.detail.hvns.detail.hvn-routes.index',
          organizationId,
          projectId,
          hvnId
        )
        .followRedirects();
    }
  });

  <template>
    <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title @lg="8">
              {{t "components.page.hvn-routes.create.title"}}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          {{#if (and this.connectionTargets this.isDisabled)}}
            <HdsAlert
              @type="inline"
              @color="warning"
              data-test-no-targets-banner
              as |A|
            >
              <A.Title>
                {{t
                  "components.page.hvn-routes.create.form.target.empty.banner.title"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.hvn-routes.create.form.target.empty.banner.message"
                }}
              </A.Description>
            </HdsAlert>
          {{/if}}
          {{#if this.save.last.error}}
            <FormError />
          {{/if}}
          {{#if this.showHubSpokeWarning}}
            <HdsAlert
              @type="inline"
              @color="warning"
              data-test-hub-spoke-banner
              as |A|
            >
              <A.Title>
                {{t
                  "components.page.hvn-routes.create.form.hub-spoke-alert.title"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.hvn-routes.create.form.hub-spoke-alert.message"
                }}
              </A.Description>
            </HdsAlert>
          {{/if}}
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="8" @lg="5">
              <form
                class="hcp-hds-form"
                data-test-form
                novalidate
                {{on "submit" this.save.perform}}
              >
                <section>
                  <FieldError @field="id" @hdsDelegatedError={{true}} as |err|>
                    <HdsFormTextInputField
                      @extraAriaDescribedBy="routeIdHelp"
                      @id="id"
                      @isInvalid={{err}}
                      @type="text"
                      @value={{this.id}}
                      name="id"
                      data-test-id
                      data-test-input
                      {{on "input" (pick "target.value" (set this "id"))}}
                      as |F|
                    >
                      <F.Label for="id">
                        {{t
                          "components.page.hvn-routes.create.form.route-id.label"
                        }}
                      </F.Label>
                      <F.HelperText id="routeId">
                        {{t
                          "components.page.hvn-routes.create.form.route-id.message"
                        }}
                      </F.HelperText>
                      {{#if err}}
                        <F.Error>
                          {{err.description}}
                        </F.Error>
                      {{/if}}
                    </HdsFormTextInputField>
                  </FieldError>
                  <br />
                  <FieldError
                    @field="destination"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @extraAriaDescribedBy="destinationHelp"
                      @id="destination"
                      @isInvalid={{err}}
                      @type="text"
                      @value={{this.cidr}}
                      name="destination"
                      placeholder={{t
                        "components.page.hvn-transit-gateway-attachments.create.form.label.vpc-cidr-block-placeholder"
                      }}
                      data-test-destination
                      data-test-input
                      {{on "input" (pick "target.value" (set this "cidr"))}}
                      as |F|
                    >
                      <F.Label for="destination">
                        {{t
                          "components.page.hvn-routes.create.form.destinations.label"
                        }}
                      </F.Label>
                      <F.HelperText id="destinationHelp">
                        {{t
                          "components.page.hvn-routes.create.form.destinations.message"
                          hvnID=@model.network.id
                          hvnCidr=@model.network.cidrBlock
                          provider=@model.network.location.region.provider
                          htmlSafe=true
                        }}
                      </F.HelperText>
                      {{#if err}}
                        <F.Error>
                          {{err.description}}
                        </F.Error>
                      {{/if}}
                    </HdsFormTextInputField>
                  </FieldError>

                  {{#if
                    (and
                      (eq @model.network.location.region.provider "aws")
                      (or this.connection.type this.connectionTarget)
                    )
                  }}
                    <div {{boxPadding "xs 0 0 0"}}>
                      <DisclosureGuide
                        @content={{this.disclosureGuideCidr}}
                        data-test-cidr-disclosure-guide={{if
                          this.connection.type
                          this.connection.type
                          this.connectionTarget.type
                        }}
                      />
                    </div>
                  {{/if}}
                  {{#if this.connection.type}}
                    <HdsAlert
                      @type="compact"
                      @color="neutral"
                      {{boxMargin "xs 0 0 0"}}
                      data-test-no-targets-banner
                      as |A|
                    >
                      <A.Description>
                        {{t
                          this.connection.info.message
                          provider=@model.network.location.region.provider
                        }}
                      </A.Description>
                      <A.LinkStandalone
                        @icon="arrow-right"
                        @iconPosition="trailing"
                        @route={{this.connection.configurationInstructions}}
                        @text={{t
                          this.connection.info.linkText
                          provider=@model.network.location.region.provider
                        }}
                        target="_blank"
                        data-test-detail-configuration-instructions-link={{this.connection.id}}
                      />
                    </HdsAlert>
                  {{/if}}

                  {{! Show select to choose targets if creating an HVN Route from the Routes tab table list }}
                  {{#if this.connectionTargets}}
                    <br />
                    <FieldError
                      @hdsDelegatedError={{true}}
                      @field="target"
                      as |err|
                    >
                      <HdsFormSelectField
                        @isInvalid={{err}}
                        disabled={{this.isDisabled}}
                        data-test-target-select
                        {{on "change" this.updateConnectionTarget}}
                        as |F|
                      >
                        <F.Label for="target">
                          {{t
                            "components.page.hvn-routes.create.form.target.label"
                          }}
                        </F.Label>
                        <F.Options>
                          <option value disabled selected>
                            {{t
                              "components.page.hvn-routes.create.form.target.select-label"
                            }}
                          </option>
                          {{! Peering Targets }}
                          {{#if this.connectionTargets.peeringTargets.length}}
                            <optgroup
                              data-test-target-group-peerings
                              label={{t
                                "components.page.hvn-routes.create.form.target.optgroup.peerings.label"
                              }}
                              id={{this.connectionsInfo.peering.type}}
                            >
                              {{#each
                                this.connectionTargets.peeringTargets
                                as |peering|
                              }}
                                <option
                                  hasHvnTarget={{if
                                    peering.target.hvnTarget
                                    true
                                    false
                                  }}
                                  data-test-hvn-route-select-option-peering
                                  value={{peering.id}}
                                >
                                  {{peering.id}}
                                </option>
                              {{/each}}
                            </optgroup>
                          {{/if}}
                          {{! TGW Attachment Targets }}
                          {{#if
                            this.connectionTargets.tgwAttachmentTargets.length
                          }}
                            <optgroup
                              label={{t
                                "components.page.hvn-routes.create.form.target.optgroup.tgws.label"
                              }}
                              id={{this.connectionsInfo.tgwAttachment.type}}
                            >
                              {{#each
                                this.connectionTargets.tgwAttachmentTargets
                                as |tgw|
                              }}
                                <option value={{tgw.id}}>
                                  {{tgw.id}}
                                </option>
                              {{/each}}
                            </optgroup>
                          {{/if}}
                        </F.Options>
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                      </HdsFormSelectField>
                    </FieldError>
                    {{#if this.connectionTarget}}
                      {{#if (variation "hcp-hvn-guided-flow")}}
                        {{#let
                          (or @model.network.location.region.provider "other")
                          as |provider|
                        }}
                          <HdsAlert
                            @type="inline"
                            @icon="info"
                            {{boxMargin "md 0 0 0"}}
                            data-test-info-message
                            as |A|
                          >
                            <A.Description>
                              {{t
                                "components.page.hvn-routes.create.form.info-alert.text"
                                provider=provider
                              }}
                              <HdsLinkInline
                                data-test-detail-configuration-instructions-link={{this.connectionTarget.id}}
                                @href={{urlFor
                                  (if
                                    (eq
                                      this.connectionTarget.type
                                      this.connectionsInfo.peering.type
                                    )
                                    this.connectionTarget.detail
                                    this.connectionTarget.configurationInstructions
                                  )
                                  this.connectionTarget.id
                                }}
                                @isHrefExternal={{true}}
                              >
                                {{t
                                  "components.page.hvn-routes.create.form.info-alert.link-text"
                                  provider=provider
                                }}
                              </HdsLinkInline>
                            </A.Description>
                          </HdsAlert>
                        {{/let}}
                      {{else}}
                        <div data-test-info-message {{boxPadding "sm 0 0 0"}}>
                          <HdsAlert
                            @type="compact"
                            @color="neutral"
                            data-test-no-targets-banner
                            as |A|
                          >
                            <A.Description>
                              {{t
                                this.connectionTarget.info.message
                                provider=@model.network.location.region.provider
                              }}
                            </A.Description>
                            <A.LinkStandalone
                              @icon="arrow-right"
                              @iconPosition="trailing"
                              @model={{this.connectionTarget.id}}
                              @route={{this.connectionTarget.configurationInstructions}}
                              @text={{t
                                this.connectionTarget.info.linkText
                                provider=@model.network.location.region.provider
                              }}
                              target="_blank"
                              data-test-detail-configuration-instructions-link={{this.connectionTarget.id}}
                            />
                          </HdsAlert>
                        </div>
                      {{/if}}
                    {{/if}}
                  {{/if}}
                  {{#if this.showHubSpokeConfig}}
                    <br />
                    <FieldError
                      @field="useHubSpoke"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormToggleGroup @name="useHubSpoke" as |T|>
                        <T.Legend>
                          {{t
                            "components.page.hvn-routes.create.form.hub-spoke.title"
                          }}
                        </T.Legend>
                        <T.ToggleField
                          @id="toggle-hub-spoke"
                          data-test-hub-spoke-toggle
                          @checked={{this.useHubSpoke}}
                          {{on "change" this.toggleUseHubSpoke}}
                          as |F|
                        >
                          <F.Label>
                            {{t
                              "components.page.hvn-routes.create.form.hub-spoke.label"
                            }}
                          </F.Label>
                          <F.HelperText>
                            {{t
                              "components.page.hvn-routes.create.form.hub-spoke.message"
                            }}
                          </F.HelperText>
                          {{#if err}}
                            <F.Error>
                              {{err.description}}
                            </F.Error>
                          {{/if}}
                        </T.ToggleField>
                      </HdsFormToggleGroup>
                    </FieldError>

                    {{#if this.useHubSpoke}}
                      <br />
                      <HdsFormSelectField
                        @isRequired={{true}}
                        @id="selectHopType"
                        @name="selectHopType"
                        data-test-next-hop-select
                        {{on
                          "change"
                          (pick "target.value" (set this "nextHopType"))
                        }}
                        as |F|
                      >
                        <F.Label>
                          {{t
                            "components.page.hvn-routes.create.form.hop-type.label"
                          }}
                        </F.Label>
                        <F.HelperText>
                          {{t
                            "components.page.hvn-routes.create.form.hop-type.message"
                          }}
                        </F.HelperText>
                        <F.Options>
                          <option value={{this.azureHopType.APPLIANCE}}>
                            {{t
                              "components.page.hvn-routes.create.form.hop-type.virtual-appliance"
                            }}
                          </option>
                          <option value={{this.azureHopType.NETWORKGATEWAY}}>
                            {{t
                              "components.page.hvn-routes.create.form.hop-type.virtual-network-gateway"
                            }}
                          </option>
                        </F.Options>
                      </HdsFormSelectField>
                      {{#if (eq this.nextHopType this.azureHopType.APPLIANCE)}}
                        <br />
                        <FieldError
                          @field="nextHopIP"
                          @hdsDelegatedError={{true}}
                          data-test-input
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="nextHopIP"
                            @isInvalid={{err}}
                            @type="text"
                            @value={{this.nextHopIP}}
                            @isRequired={{true}}
                            name="nextHopIP"
                            placeholder={{t
                              "components.page.hvn-routes.create.form.hop-type-ip.placeholder"
                            }}
                            data-test-input
                            data-test-next-hop-ip
                            {{on
                              "input"
                              (pick "target.value" (set this "nextHopIP"))
                            }}
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.hvn-routes.create.form.hop-type-ip.label"
                              }}
                            </F.Label>
                            <F.HelperText>
                              {{t
                                "components.page.hvn-routes.create.form.hop-type-ip.message"
                              }}
                            </F.HelperText>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>
                      {{/if}}
                    {{/if}}
                  {{/if}}
                </section>
                {{#unless (variation "hcp-hvn-guided-flow")}}
                  <UpNext
                    @resourceType={{this.resourceType}}
                    @provider={{@model.network.location.region.provider}}
                  />
                {{/unless}}
                <footer {{boxMargin "lg 0 0 0"}}>
                  <HdsButtonSet>
                    <HdsButton
                      @color="primary"
                      @text={{if
                        (variation "hcp-hvn-guided-flow")
                        (t "components.form.create")
                        (t "components.page.hvn-routes.create.title")
                      }}
                      disabled={{this.save.isRunning}}
                      type="submit"
                      data-test-submit
                    />
                    <HdsButton
                      @color="secondary"
                      @route={{if
                        this.connection
                        this.connection.detail
                        this.hvnRouteNames.list
                      }}
                      @text={{t "components.form.cancel"}}
                      data-test-cancel
                    />
                  </HdsButtonSet>
                </footer>
              </form>
            </G.Item>
            {{#if (variation "hcp-hvn-guided-flow")}}
              <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                <div {{boxMargin "2xl 0 0 0"}}>
                  <Typography
                    @variant="h2"
                    class={{classNames
                      "hds-typography-display-300"
                      "hds-font-weight-bold"
                      "hds-foreground-primary"
                    }}
                    {{boxMargin "0 0 lg 0"}}
                  >
                    {{t "components.page.hvn-routes.create.table.title"}}
                  </Typography>
                  <HdsTable
                    @caption={{t "components.page.hvn-routes.list.title"}}
                    @valign="middle"
                    data-test-table
                  >
                    <:head as |H|>
                      <H.Tr>
                        <H.Th>
                          {{t
                            "components.page.hvn-routes.list.table.headers.route-id"
                          }}
                        </H.Th>
                        <H.Th>
                          {{t
                            "components.page.hvn-routes.list.table.headers.status"
                          }}
                        </H.Th>
                        <H.Th>
                          {{t
                            "components.page.hvn-routes.list.table.headers.destination"
                          }}
                        </H.Th>
                        <H.Th>
                          {{t
                            "components.page.hvn-routes.list.table.headers.target"
                          }}
                        </H.Th>
                        <H.Th>
                          {{t
                            "components.page.hvn-routes.list.table.headers.target-type"
                          }}
                        </H.Th>
                      </H.Tr>
                    </:head>
                    <:body as |B|>
                      {{#each @model.routes key="id" as |route|}}
                        <B.Tr data-test-hvn-routes-list-item>
                          <B.Td>
                            {{#if
                              (eq
                                route.target.hvnConnection.type
                                (resourceTypeFor "network")
                              )
                            }}
                              {{t
                                "components.page.hvn-routes.list.network-default-route-id"
                              }}
                            {{else}}
                              <Flex
                                @alignItems="center"
                                @direction="row"
                                @gap="2xs"
                              >
                                <span>{{route.id}}</span>
                                {{#if route.isLocked}}
                                  <HdsIcon
                                    @name="lock-fill"
                                    @isInline={{true}}
                                    {{tooltip
                                      (t
                                        "components.page.hvn-routes.list.tooltip.locked-route"
                                      )
                                      options=(hash
                                        placement="top" offset=(array 0 10)
                                      )
                                    }}
                                    data-test-peering-locked
                                  />
                                {{/if}}
                              </Flex>
                            {{/if}}
                          </B.Td>
                          <B.Td>
                            {{#let
                              (optionForSourceState "hvn_route" route.state)
                              as |option|
                            }}
                              <HdsBadge
                                @text={{t option.text}}
                                @color={{option.color}}
                                @icon={{option.icon}}
                                @isIconOnly={{option.isIconOnly}}
                                @size={{option.size}}
                                @type={{option.type}}
                              />
                            {{/let}}
                          </B.Td>
                          <B.Td>
                            {{route.destination}}
                          </B.Td>
                          <B.Td>
                            {{! TODO there has to be a way to put this into some sort of helper  }}
                            {{#if route.isLocked}}
                              {{route.target.hvnConnection.id}}
                            {{else}}
                              {{#if
                                (eq
                                  route.target.hvnConnection.type
                                  this.connectionsInfo.tgwAttachment.type
                                )
                              }}
                                <HdsLinkInline
                                  @model={{route.target.hvnConnection.id}}
                                  @route={{this.connectionsInfo.tgwAttachment.detail}}
                                >
                                  {{route.target.hvnConnection.id}}
                                </HdsLinkInline>
                              {{else if
                                (eq
                                  route.target.hvnConnection.type
                                  this.connectionsInfo.peering.type
                                )
                              }}
                                <HdsLinkInline
                                  @model={{route.target.hvnConnection.id}}
                                  @route={{this.connectionsInfo.peering.detail}}
                                >
                                  {{route.target.hvnConnection.id}}
                                </HdsLinkInline>
                              {{else}}
                                {{route.target.hvnConnection.id}}
                              {{/if}}
                            {{/if}}
                          </B.Td>
                          <B.Td>
                            <Flex
                              @alignItems="center"
                              @direction="row"
                              @gap="2xs"
                            >
                              {{#if
                                (eq
                                  route.target.hvnConnection.type
                                  "hashicorp.network.hvn"
                                )
                              }}
                                <HdsIcon
                                  @name="hashicorp-color"
                                  @isInline={{true}}
                                  data-test-provider-logo="hashicorp-color"
                                />
                                <span>
                                  {{resourceTypeDisplayName
                                    route.target.hvnConnection.type
                                  }}
                                </span>
                              {{else}}
                                {{#let
                                  (optionForSourceState
                                    "region"
                                    @model.network.location.region.provider
                                  )
                                  as |option|
                                }}
                                  <HdsIcon
                                    @name={{option.icon}}
                                    @isInline={{true}}
                                    data-test-provider-logo={{@model.network.location.region.provider}}
                                  />
                                  <span>
                                    {{resourceTypeDisplayName
                                      route.target.hvnConnection.type
                                    }}
                                  </span>
                                {{/let}}
                              {{/if}}
                            </Flex>
                          </B.Td>
                        </B.Tr>
                      {{/each}}
                    </:body>
                  </HdsTable>
                </div>
              </G.Item>
            {{/if}}
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </WithErrors>
  </template>
}
